import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import ServiceAPI from "../services/NavaService";

export default function ClientSales() {
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [approved, setApproved] = useState([]);
    const [declined, setDeclined] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [saleHeader, setSaleHeader] = useState(null);
    const [saleDetails, setSaleDetails] = useState([]);

    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : null;
    const usrId = user?.usrId ?? user?.id ?? null;

    useEffect(() => {
        loadAll();
    }, []);

    const customAlert = (text, icon = "info") => {
        Swal.fire({
            text,
            icon,
            didOpen: () => {
                const container = document.querySelector(".swal2-container");
                const popup = document.querySelector(".swal2-popup");
                if (container) container.style.zIndex = "10010";
                if (popup) popup.style.zIndex = "10011";
            },
        });
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            // Assumes ServiceAPI provides these client-scoped methods that use the logged-in usrId internally
            const [pendRes, apprRes, declRes] = await Promise.allSettled([
                ServiceAPI.getPendingClientSales(usrId),
                ServiceAPI.getApprovedClientSales(usrId),
                ServiceAPI.getDeclinedClientSales(usrId),
            ]);

            setPending(pendRes.status === "fulfilled" && Array.isArray(pendRes.value.data) ? pendRes.value.data : []);
            setApproved(apprRes.status === "fulfilled" && Array.isArray(apprRes.value.data) ? apprRes.value.data : []);
            setDeclined(declRes.status === "fulfilled" && Array.isArray(declRes.value.data) ? declRes.value.data : []);
        } catch (err) {
            console.error(err);
            customAlert("Failed to load client sales", "error");
            setPending([]);
            setApproved([]);
            setDeclined([]);
        } finally {
            setLoading(false);
            setPage(1);
        }
    };

    const currentList = tab === "pending" ? pending : tab === "approved" ? approved : declined;

    const filtered = (currentList || []).filter((s) => {
        const q = (search || "").trim().toLowerCase();
        if (!q) return true;
        const email = (s.email || s.clientEmail || "").toString().toLowerCase();
        const id = (s.nshId ?? s.id ?? s.nsh_id ?? "").toString().toLowerCase();
        return email.includes(q) || id.includes(q);
    });

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

    // fetch details and show modal (read-only)
    const openViewModal = async (hdr) => {
        setIsModalOpen(true);
        setSaleHeader(null);
        setSaleDetails([]);

        const nshId = hdr.nshId ?? hdr.id ?? hdr.nsh_id ?? hdr.nrgSaleId ?? null;
        setSaleHeader(hdr); // show header immediately

        if (!nshId) {
            customAlert("Sale header id missing", "error");
            return;
        }

        try {
            const res = await ServiceAPI.getSaleDetalsBySaleHeader(nshId);
            const list = Array.isArray(res.data) ? res.data : [];
            const normalized = list.map((d) => ({
                ...d,
                nsdId: d.nsdId ?? d.id ?? d.nsd_id ?? d.nrgSaleDetailId ?? null,
                srvId: d.srvId ?? d.srv_id ?? d.serviceId ?? null,
                salePrice: d.salePrice ?? d.sale_price ?? d.price ?? 0,
                saleUnits: d.saleUnits ?? d.sale_units ?? d.units ?? 0,
                serviceName: d.serviceName ?? d.service_name ?? d.name ?? "",
            }));
            setSaleDetails(normalized);
        } catch (err) {
            console.error(err);
            customAlert("Failed to load sale details", "error");
            setSaleDetails([]);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSaleHeader(null);
        setSaleDetails([]);
    };

    const computeTotalFromDetails = (details) =>
        details.reduce((acc, d) => {
            const price = parseFloat(d.salePrice) || 0;
            const units = parseFloat(d.saleUnits) || 0;
            return acc + price * units;
        }, 0);

    const formatCurrency = (v) => {
        const n = parseFloat(v) || 0;
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <>
            <TopNavbar />
            <PageWrapper style={{ marginTop: "5%" }}>
                <TopRow>
                    <h2>My Sales</h2>
                    <div className="controls">
                        <TabBtn active={tab === "pending"} onClick={() => { setTab("pending"); setSearch(""); setPage(1); }}>
                            Pending
                        </TabBtn>
                        <TabBtn active={tab === "approved"} onClick={() => { setTab("approved"); setSearch(""); setPage(1); }}>
                            Approved
                        </TabBtn>
                        <TabBtn active={tab === "declined"} onClick={() => { setTab("declined"); setSearch(""); setPage(1); }}>
                            Declined
                        </TabBtn>

                        <input
                            type="text"
                            placeholder="Search id or email..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                        <OutlineBtn onClick={() => { setSearch(""); setPage(1); }}>Clear</OutlineBtn>
                    </div>
                </TopRow>

                <TableWrap>
                    {loading ? (
                        <div>Loading…</div>
                    ) : (
                        <>
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Email</th>
                                        <th style={{ textAlign: "right" }}>Total</th>
                                        <th>Approved</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageItems.map((s) => {
                                        const id = s.nshId ?? s.id ?? s.nsh_id ?? "-";
                                        const email = s.email ?? s.clientEmail ?? "";
                                        const total = s.totalSalePrice ?? s.total_sale_price ?? 0;
                                        const approvedYn = (s.approvedYn ?? s.approved_yn ?? s.approved) || "P";
                                        return (
                                            <tr key={id}>
                                                <td>{id}</td>
                                                <td title={email}>{email}</td>
                                                <td style={{ textAlign: "right" }}>{formatCurrency(total ?? computeTotalFromDetails([]))}</td>
                                                <td>{approvedYn === "Y" ? "Yes" : approvedYn === "N" ? "No" : "Pending"}</td>
                                                <td className="actions">
                                                    <PrimarySmall onClick={() => openViewModal(s)}>View</PrimarySmall>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {pageItems.length === 0 && (
                                        <tr>
                                            <td colSpan="5">No sales found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            <Pagination>
                                <OutlineSmall disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</OutlineSmall>
                                <span>Page {page} / {pageCount}</span>
                                <OutlineSmall disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next</OutlineSmall>
                            </Pagination>
                        </>
                    )}
                </TableWrap>

                {/* View-only Modal */}
                {isModalOpen && saleHeader && (
                    <ModalOverlay onClick={closeModal}>
                        <ModalContent onClick={(e) => e.stopPropagation()}>
                            <ModalHeader>
                                <h3>View Sale</h3>
                                <button onClick={closeModal}>Close</button>
                            </ModalHeader>

                            <ModalBody>
                                <label>ID</label>
                                <IdBox>{saleHeader.nshId ?? saleHeader.id ?? saleHeader.nsh_id}</IdBox>

                                <label>Email</label>
                                <input type="text" value={saleHeader.email ?? saleHeader.clientEmail ?? ""} readOnly />

                                <label>Approved</label>
                                <select value={saleHeader.approvedYn ?? saleHeader.approved_yn ?? "P"} readOnly>
                                    <option value="P">Pending</option>
                                    <option value="Y">Approved</option>
                                    <option value="N">Declined</option>
                                </select>

                                <label>Total Sale Price</label>
                                <input type="text" value={formatCurrency(computeTotalFromDetails(saleDetails))} readOnly />

                                <div style={{ marginTop: 12, fontWeight: 700 }}>Sale Details</div>

                                <div style={{ overflowX: "auto", marginTop: 8 }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th>nsdId</th>
                                                <th>Service</th>
                                                <th style={{ textAlign: "right" }}>Sale Price</th>
                                                <th style={{ textAlign: "right" }}>Units</th>
                                                <th style={{ textAlign: "right" }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {saleDetails.map((d) => {
                                                const nsdId = d.nsdId ?? d.id ?? d.nsd_id ?? "-";
                                                const svcName = d.serviceName ?? d.service_name ?? d.name ?? "";
                                                const salePrice = d.salePrice ?? 0;
                                                const saleUnits = d.saleUnits ?? 0;
                                                const lineTotal = (parseFloat(salePrice) || 0) * (parseFloat(saleUnits) || 0);
                                                return (
                                                    <tr key={nsdId}>
                                                        <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>{nsdId}</td>
                                                        <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>{svcName}</td>
                                                        <td style={{ padding: 8, borderBottom: "1px solid #eee", textAlign: "right" }}>{formatCurrency(salePrice)}</td>
                                                        <td style={{ padding: 8, borderBottom: "1px solid #eee", textAlign: "right" }}>{saleUnits}</td>
                                                        <td style={{ padding: 8, borderBottom: "1px solid #eee", textAlign: "right" }}>{formatCurrency(lineTotal)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </ModalBody>

                            <ModalFooter>
                                <OutlineBtn onClick={closeModal}>Close</OutlineBtn>
                            </ModalFooter>
                        </ModalContent>
                    </ModalOverlay>
                )}

            </PageWrapper>
        </>
    );
}

/* ---------- styles reused from your admin pages ---------- */

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1180px;
  margin: 0 auto;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #222;
  h2 { margin: 0 0 12px 0; font-size:20px; font-weight:700; }
  .controls { display:flex; gap:10px; align-items:center; margin-bottom:12px; }
  input[type="text"] { padding:10px 12px; border:1px solid #e7e7f0; border-radius:8px; min-width:260px; }
`;

const TabBtn = styled.button`
  padding:8px 12px;
  border-radius:8px;
  border:1px solid #e6e7ef;
  background: ${(p) => (p.active ? "linear-gradient(180deg,#6a2cff,#5722c9)" : "#fff")};
  color: ${(p) => (p.active ? "#fff" : "#333")};
  cursor:pointer;
  font-weight:700;
  box-shadow: ${(p) => (p.active ? "0 8px 20px rgba(102,51,204,0.12)" : "none")};
`;

const PrimaryBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:0;
  background: linear-gradient(180deg,#6a2cff,#5a22c9);
  color:#fff;
  cursor:pointer;
  font-weight:600;
  box-shadow: 0 8px 20px rgba(88,34,200,0.12);
`;

const OutlineBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:1px solid #e9e9f4;
  background:#fff;
  color:#222;
  cursor:pointer;
`;

const TableWrap = styled.div`
  background:#fff;
  border:1px solid #f0f1f6;
  border-radius:10px;
  max-height: calc(100vh - 320px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  table {
    width:100%;
    border-collapse:collapse;
    font-size:14px;
    table-layout: fixed;
    min-width:900px;
  }

  thead th {
    position:sticky;
    top:0;
    background: linear-gradient(180deg,#fff,#fbfbfe);
    z-index: 3;
    padding:12px;
    text-align:left;
    font-weight:700;
    border-bottom:1px solid #f2f4f8;
  }

  th, td {
    padding:10px 12px;
    border-bottom:1px solid #f6f7fb;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  td[title] { cursor: help; }

  td.actions {
    white-space: nowrap;
    width: 140px;
  }

  tbody tr:hover { background:#fbfcff; }

  @media (max-width:1000px) {
    table { min-width: 820px; }
    th, td { padding:9px; font-size:13px; }
  }
`;

const Pagination = styled.div`
  display:flex;
  justify-content:center;
  align-items:center;
  gap:10px;
  padding:12px 0;
  font-size:14px;
`;

const ModalOverlay = styled.div`
  position:fixed; inset:0; background: rgba(6,8,20,0.48);
  display:flex; align-items:center; justify-content:center; z-index:5000; padding:16px;
`;

const ModalContent = styled.div`
  width:820px; max-width:96%; max-height:92vh; overflow:auto; background:#fff; border-radius:12px; padding:18px;
  box-shadow: 0 30px 80px rgba(18,24,64,0.16);
`;

const ModalHeader = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;
  h3 { margin:0; font-size:18px; font-weight:700; }
  button { background:transparent; border:0; color:#666; font-size:15px; cursor:pointer; }
`;

const ModalBody = styled.div`
  display:flex; flex-direction:column; gap:10px;
  label { font-weight:600; font-size:13px; margin-top:8px; color:#222; }
  input[type="text"], select, textarea { padding:10px; border:1px solid #e9e9f2; border-radius:8px; font-size:14px; }
  textarea { min-height:140px; resize:vertical; }
`;

const IdBox = styled.div`
  padding:8px 10px; background:#f7f8fb; border-radius:8px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace;
`;

const ModalFooter = styled.div`
  display:flex; justify-content:flex-end; gap:10px; margin-top:12px;
`;

const PrimarySmall = styled.button`
  padding:7px 10px; border-radius:8px; border:0; background:#6a2cff; color:#fff; cursor:pointer; margin-right:6px; white-space:nowrap;
`;
const OutlineSmall = styled.button`
  padding:7px 10px; border-radius:8px; border:1px solid #e9e9f4; background:#fff; color:#222; cursor:pointer;
`;

const TopRow = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; gap:12px;

  h2 { margin:0; font-size:20px; }

  .controls { display:flex; gap:8px; align-items:center; }

  @media (max-width:720px) {
    flex-direction:column; align-items:flex-start;
    .controls { width:100%; display:flex; gap:8px; flex-wrap:wrap; }
  }
`;
